/**
 * F2WCustomLogger - Unified logging service for Fly2Work web application
 *
 * Environment Behavior:
 * - Jest Test (NODE_ENV === 'test'): All logs suppressed
 * - Development/Test/Pre: All log levels output to console
 * - Production: Only ERROR logs output to console
 * - Datadog RUM: Captures errors in ALL environments
 *
 * Shared Logger Interface (aligned with TppCustomLogger in GPX Web):
 * - info(message, context)   — Informational messages
 * - warn(message, context)   — Warning messages
 * - error(message, error, context) — Error messages with Error object
 * - debug(message, context)  — Debug messages
 *
 * Standard context structure:
 * @typedef {Object} LogContext
 * @property {string} component - Name of the component/service originating the log
 * @property {string} action    - Name of the method or action being performed
 * @property {Object} details   - Additional data specific to the log entry
 * @property {string} timestamp - ISO 8601 timestamp (auto-added by error method)
 *
 * @param {string} prefix - Configurable prefix for log messages (e.g., "F2W", "TPP", "MTP")
 */
class F2WCustomLogger {
  /**
   * Creates a new F2WCustomLogger instance
   * @param {string} prefix - The prefix to use in log messages (default: 'F2W')
   */
  constructor(prefix = 'F2W') {
    this.prefix = prefix;

    // Environment detection using NODE_ENV and REACT_APP_ENVIRONMENT
    // Requirement 1.9: Detect environment using combination of variables
    this.isJestTest = process.env.NODE_ENV === 'test';
    this.isProduction = process.env.REACT_APP_ENVIRONMENT === 'prod' || process.env.NODE_ENV === 'production';
    this.isDevelopment = process.env.NODE_ENV === 'development';

    // Additional environment flags for test/pre environments
    this.isTestPre = process.env.REACT_APP_ENVIRONMENT === 'test' || process.env.REACT_APP_ENVIRONMENT === 'pre';
  }

  log(message, context = {}) {
    if (this.isJestTest) return;
    // In production, suppress non-error logs (Requirement 1.6)
    if (this.isProduction) return;
    // In dev/test/pre environments, output to console (Requirement 1.5)
    console.log('[%s LOG]', this.prefix, message, context);
  }

  error(message, error = null, context = {}) {
    if (this.isJestTest) return;

    const errorContext = {
      timestamp: new Date().toISOString(),
      ...context,
    };

    // Console output in dev/test/pre environments with full stack trace (Requirement 1.3)
    // In production, still log errors to console (Requirement 1.6 - only ERROR logs in prod)
    console.error('[%s ERROR]', this.prefix, message, {
      error: error?.message || error,
      stack: error?.stack,
      ...errorContext,
    });

    // Datadog RUM - send in ALL environments (Requirement 1.7)
    if (window.DD_RUM) {
      try {
        window.DD_RUM.addError(error || new Error(String(message)), errorContext);
      } catch (ddError) {
        // Fail silently - don't break the app if Datadog fails
        console.warn('[%s] Failed to send error to Datadog', this.prefix, ddError);
      }
    }
  }

  warn(message, context = {}) {
    if (this.isJestTest) return;
    // In production, suppress non-error logs (Requirement 1.6)
    if (this.isProduction) return;
    // In dev/test/pre environments, output to console (Requirement 1.5)
    console.warn('[%s WARN]', this.prefix, message, context);
  }

  info(message, context = {}) {
    if (this.isJestTest) return;
    // In production, suppress non-error logs (Requirement 1.6)
    if (this.isProduction) return;
    // In dev/test/pre environments, output to console (Requirement 1.5)
    console.info('[%s INFO]', this.prefix, message, context);
  }

  debug(message, context = {}) {
    if (this.isJestTest) return;
    // In production, suppress non-error logs (Requirement 1.6)
    if (this.isProduction) return;
    // In dev/test/pre environments, output to console (Requirement 1.5)
    console.debug('[%s DEBUG]', this.prefix, message, context);
  }

  apiError(endpoint, error, context = {}) {
    if (this.isJestTest) return;

    // Sanitize endpoint to prevent format string injection
    const sanitizedEndpoint = String(endpoint).replace(/%/g, '%%');

    // Build structured context with endpoint, status code, and timestamp (Requirement 1.8)
    const apiErrorContext = {
      endpoint: sanitizedEndpoint,
      status: error?.response?.status || null,
      timestamp: new Date().toISOString(),
      source: 'api',
      ...context,
    };

    // Console output with full context (Requirement 1.8)
    // In production, still log API errors to console (Requirement 1.6 - only ERROR logs in prod)
    console.error('[%s API_ERROR] %s', this.prefix, sanitizedEndpoint, {
      error: error?.message || error,
      stack: error?.stack,
      ...apiErrorContext,
    });

    // Datadog RUM - send in ALL environments (Requirement 1.7)
    if (window.DD_RUM) {
      try {
        const apiError = error || new Error('API Error: '.concat(sanitizedEndpoint));
        window.DD_RUM.addError(apiError, apiErrorContext);
      } catch (ddError) {
        // Fail silently - don't break the app if Datadog fails
        console.warn('[%s] Failed to send API error to Datadog', this.prefix, ddError);
      }
    }
  }
}

// Export the class for creating custom instances with different prefixes
export { F2WCustomLogger };

// Export a default singleton instance with 'F2W' prefix for backward compatibility
export default new F2WCustomLogger('F2W');
